import { useState, useCallback } from 'react';
import { searchContactBook, ContactBookResult } from '@/services/api/contacts';
import { useDebounceSearch } from './useDebounceSearch';

export const useContactBook = () => {
  const [contacts, setContacts] = useState<ContactBookResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Detect if query is phone or email
  const detectQueryType = (query: string): 'phone' | 'email' | null => {
    if (!query.trim()) return null;
    
    // Email detection
    if (query.includes('@') || query.includes('.')) {
      return 'email';
    }
    
    // Phone detection (contains digits, +, -, spaces, parentheses)
    if (/^[\d\s\-\+\(\)]+$/.test(query.replace(/\s/g, ''))) {
      return 'phone';
    }
    
    return null;
  };

  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setContacts([]);
      setLoading(false);
      setError(null);
      return;
    }

    const queryType = detectQueryType(query);
    if (!queryType) {
      setContacts([]);
      setLoading(false);
      setError(null);
      return;
    }

    // Minimum length validation
    const minLength = queryType === 'phone' ? 5 : 3;
    if (query.replace(/\s/g, '').length < minLength) {
      setContacts([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log(`🔍 Searching contact book: ${query} (${queryType})`);
      const response = await searchContactBook(query, queryType);
      console.log('✅ Contact book results:', response.contacts || []);
      setContacts(response.contacts || []);
    } catch (err) {
      console.error('💥 Error searching contact book:', err);
      setError('Failed to search contacts');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const { debouncedSearch } = useDebounceSearch(performSearch, { 
    minLength: 3, 
    delay: 300 
  });

  const searchContacts = useCallback((query: string) => {
    debouncedSearch(query);
  }, [debouncedSearch]);

  const clearResults = useCallback(() => {
    setContacts([]);
    setError(null);
    setLoading(false);
  }, []);

  return { 
    contacts, 
    loading, 
    error,
    searchContacts,
    clearResults
  };
};